import { useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { capitalize } from "lodash";
import { useLocation } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import {
  ResizableSidepaneProvider,
  useResizableSidepane,
} from "src/base-components/ResizableSidepane";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { EmptyState } from "src/design-system/EmptyState";
import { EntityDetailsSidePane } from "src/entities/entityView/EntityDetailsSidePane";
import { EntityTabs } from "src/entities/entityView/EntityTabs";
import { EntityViewSidePane } from "src/entities/entityView/EntityViewSidePane";
import { EntityTabIds } from "src/entities/entityView/useEntityTabRefresh";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import {
  EntityResource,
  useEntity,
  useEntityNavigation,
  useEntitySchemas,
} from "src/entities/queries";
import { ExtendedHistoricalDecisionApiResponse } from "src/flow/decisionHistory/Columns";
import { useEnvironment } from "src/hooks/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import {
  EntityViewParams,
  getUrlToEntityDetailPage,
  getUrlToSchemaEntitiesPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type EntityViewProps = {
  tab: EntityTabIds;
};

export const EntityViewWrapper = ({ tab }: EntityViewProps) => {
  return (
    <ResizableSidepaneProvider>
      <EntityView tab={tab} />
    </ResizableSidepaneProvider>
  );
};

const buildPageCursor = (env: string, entity: EntityResource | undefined) => {
  return entity
    ? `[${entity?._updated_at}, "${env}.${entity?._schema}.${entity?.properties.id}"]`
    : undefined;
};

export const EntityView = ({ tab }: EntityViewProps) => {
  const [selectedDecision, setSelectedDecision] =
    useState<ExtendedHistoricalDecisionApiResponse | null>(null);
  const {
    schema: schemaId,
    id: entityId,
    orgId,
    wsId,
  } = useParamsDecode<EntityViewParams>();
  const { workspace } = useWorkspaceContext();
  const baseUrl = workspace.base_url ?? "";
  const location = useLocation();
  const [env] = useEnvironment();

  const {
    data: entity,
    error,
    isSuccess: isEntityLoadSuccess,
  } = useEntity({
    schema: schemaId,
    env,
    entityId,
  });

  const { data: schemasData, isSuccess: isSchemasLoadSuccess } =
    useEntitySchemas({
      baseUrl,
      options: { enabled: !!baseUrl && isEntityLoadSuccess },
    });

  const cursor = buildPageCursor(env, entity);

  const { data: nextPageData, isLoading: isNextPageLoading } =
    useEntityNavigation({
      schema: schemaId,
      env,
      pageCursor: cursor,
      options: { enabled: isEntityLoadSuccess && !!cursor },
    });

  const { data: prevPageData, isLoading: isPrevPageLoading } =
    useEntityNavigation({
      schema: schemaId,
      env,
      pageCursor: cursor,
      pageDirection: "backward",
      options: { enabled: isEntityLoadSuccess && !!cursor },
    });

  const entitiesLoading = isNextPageLoading || isPrevPageLoading;

  const prevEntity = prevPageData?.entities[0];
  const nextEntity = nextPageData?.entities[0];

  const schema = findSchema(schemaId, schemasData);

  const buildBreadcrumbUrl = (schema: string) =>
    getUrlToSchemaEntitiesPage(orgId, wsId, schema);

  const { width, isResizing } = useResizableSidepane();

  const getEntityUrl = (entityId: string) =>
    getUrlToEntityDetailPage(orgId, wsId, schemaId, entityId);

  const breadcrumbs = [
    {
      key: "entity-schema",
      breadcrumb: (
        <PageHeader.Breadcrumb
          dataLoc="entity-schema"
          href={buildBreadcrumbUrl(schema ? schemaId : "")}
          label={capitalize(schema?._display_name_plural ?? "")}
          hideIcon
        />
      ),
    },
    {
      key: "entity-name",
      breadcrumb: entitiesLoading ? (
        <SkeletonPlaceholder height="h-4" width="w-24" />
      ) : (
        <PageHeader.Breadcrumb
          dataLoc="entity-name"
          href={`${location.pathname}${location.search}`}
          label={
            (entity &&
              schema &&
              entity?.properties[schema._primary_property]?.toString()) ||
            ""
          }
          active
        />
      ),
    },
  ];

  const navigation = {
    previous: {
      url: prevEntity?._id ? getEntityUrl(prevEntity._id) : "",
      disabled: !prevEntity?._id,
    },
    next: {
      url: nextEntity?._id ? getEntityUrl(nextEntity._id) : "",
      disabled: !nextEntity?._id,
    },
  };

  const schemaNotFound = !schema && isSchemasLoadSuccess;
  const entityNotFound = error?.status === 404;

  return (
    <FloatingWindowsProvider>
      <div
        className={twMerge(
          "flex h-full flex-col",
          isResizing && "cursor-col-resize",
        )}
      >
        <PageHeader
          breadcrumbs={breadcrumbs}
          navigation={entitiesLoading ? undefined : navigation}
        />
        <div className="flex min-h-0 flex-1">
          {schemaNotFound || entityNotFound ? (
            <NotFoundState
              schemaIcon={schema ? getSchemaIcon(schema) : undefined}
              schemaNamePlural={schema?._display_name_plural}
              schemaNameSingular={schema?._display_name_singular}
              type={schemaNotFound ? "schema" : "entity"}
            />
          ) : (
            <>
              <div className="h-full w-90 shrink-0 border-gray-200 border-l bg-white p-5">
                <EntityDetailsSidePane
                  entityId={entityId}
                  environment={env}
                  schemaId={schemaId}
                  isWindowPill
                />
              </div>
              <EntityTabs
                selectedDecision={selectedDecision}
                tab={tab}
                onSelectDecision={setSelectedDecision}
              />
              {Boolean(selectedDecision) && (
                // placeholder for the sidepane, assigning width to sync with the resizable sidepane
                <div className="flex-shrink-0 p-5" style={{ width }} />
              )}
              <EntityViewSidePane
                selectedDecision={selectedDecision}
                setSelectedDecision={setSelectedDecision}
              />
            </>
          )}
        </div>
      </div>
    </FloatingWindowsProvider>
  );
};

const NotFoundState = ({
  type,
  schemaNamePlural,
  schemaNameSingular,
  schemaIcon,
}: {
  type: "schema" | "entity";
  schemaNamePlural: string | undefined;
  schemaNameSingular: string | undefined;
  schemaIcon: IconProp | undefined;
}) => {
  const { orgId, wsId, schema } = useParamsDecode<EntityViewParams>();

  if (type === "schema") {
    return (
      <div className="w-full p-5">
        <WhitePane>
          <EmptyState
            actionButton={{
              text: "Go to entities",
              linkTo: getUrlToSchemaEntitiesPage(orgId, wsId, ""),
            }}
            description="The entity type you are looking for does not exist"
            headline="Entity type not found"
            icon={faMagnifyingGlass}
          />
        </WhitePane>
      </div>
    );
  }

  return (
    <div className="w-full p-5">
      <WhitePane>
        <EmptyState
          actionButton={{
            text: `Go to ${schemaNamePlural?.toLowerCase()}`,
            linkTo: getUrlToSchemaEntitiesPage(orgId, wsId, schema),
          }}
          description={`The ${schemaNameSingular} you are looking for does not exist`}
          headline={`${capitalize(schemaNameSingular)} not found`}
          icon={schemaIcon ?? faMagnifyingGlass}
        />
      </WhitePane>
    </div>
  );
};
